import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { UpdateCustomerBodyDto, UpdateSubscriptionBody } from '@nusign/picflow-backend-types';
import { PaymentIntent, StripeError } from '@stripe/stripe-js';

import { firstValueFrom, lastValueFrom, tap } from 'rxjs';
import type { Stripe } from 'stripe';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { Promotion } from '../types/promotion.type';
import { Proration } from '../types/proration';
import { DataLoaderService } from './data-loader.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PlanService } from './plan.service';
import { PricingService } from './pricing.service';
import { PromotionService } from './promotion.service';
import { SentryService } from './sentry.service';
import { StateService } from './state.service';
import { StripeService } from './stripe.service';
import { ToastService } from './toast.service';

export type changePlanResp = Promise<boolean | { error: StripeError; retry: () => boolean | changePlanResp }>;

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private pricingService = inject(PricingService);
  private state = inject(StateService);
  private loader = inject(DataLoaderService);
  private promotionService = inject(PromotionService);
  private toasts = inject(ToastService);
  private stripeService = inject(StripeService);
  private planService = inject(PlanService);
  private sentryService = inject(SentryService);
  private observableLoader = inject(ObservableDataLoaderService);

  public async handlePayment(payment_intent: PaymentIntent) {
    if (payment_intent.status !== 'requires_action') return true;
    const { error } = await this.confirmCardPayment(payment_intent.client_secret);

    if (!error) return true;
    console.error(error);
    // Sentry.captureException('failed_to_confirm_3d_secure_subscription_update');
    this.toasts.add('Payment confirmation failed', 'error');
    return {
      error,
      retry: async () => {
        return await this.handlePayment(payment_intent);
      },
    };
  }

  private async confirmCardPayment(secret: string) {
    return this.stripeService.confirmCardPayment(secret, {
      payment_method: this.state.paymentMethod,
    });
  }

  private async updateSubscription(params: UpdateSubscriptionBody) {
    const resp = await lastValueFrom(
      this.http.patch<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`, params)
    );

    await this.refreshPermissions();
    if (!(resp.latest_invoice as Stripe.Invoice).payment_intent) return true;
    return this.handlePayment((resp.latest_invoice as Stripe.Invoice).payment_intent as unknown as PaymentIntent);
  }

  public async changePromotion(promotion_code: string) {
    return await this.updateSubscription({ promotion_code });
  }

  public async createSubscription(price: Stripe.Price, promo?: Promotion, startTrial = false) {
    const prices: { id: string; quantity?: number }[] = [{ id: price.id }];
    try {
      const trialLength = this.pricingService.getTrialLengthDays();
      const params = {
        prices,
        trial_days: startTrial ? trialLength : 0,
        promotion_code: promo?.code,
      };

      const resp = await lastValueFrom(
        this.http.post<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`, params)
      );

      if (!(resp.latest_invoice as Stripe.Invoice).payment_intent) return true;
      return this.handlePayment((resp.latest_invoice as Stripe.Invoice).payment_intent as unknown as PaymentIntent);
    } catch (err) {
      return this.createOrupdateSubscriptionError(err);
    }
  }

  public async changePlan(
    price: Stripe.Price,
    promo?: Promotion,
    startTrial = false,
    endTrialEarly = false
  ): changePlanResp {
    const prices: { id: string; quantity?: number }[] = [{ id: price.id }];
    try {
      const trialLength = this.pricingService.getTrialLengthDays();
      return await this.updateSubscription({
        prices,
        trial_days: startTrial ? trialLength : endTrialEarly ? 0 : undefined,
        promotion_code: promo?.code,
      });
    } catch (err) {
      return this.createOrupdateSubscriptionError(err);
    }
  }

  private async refreshPermissions() {
    // timeout to allow the backend to update the subscription from the stripe webhook
    await sleep(1000);
    this.observableLoader.forceDataRefreshFuzzy('tenant_permissions_');
    await sleep(1000);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private createOrupdateSubscriptionError(err: any) {
    const message = ((): string => {
      if (!err?.error?.message)
        return 'There was an error charging your card. Please update your payment method or contact support';
      const error: string = err.error.message;
      if (error.includes('requires additional user action'))
        return 'There was an error charging your card. Please update your payment method or contact support';
      return err.error?.message;
    })();
    // const message = err?.error?.message || 'There was an error charging your card.';
    this.toasts.add(message, 'error', { duration: 5 * 1000 });
    console.error(err);
    this.sentryService.captureException('change-plan-failed');
    return false;
  }

  public async changeAddonCount(priceId: string, quantity: number) {
    const prices = [{ id: priceId, quantity, deleted: quantity === 0 ? true : undefined }];
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/billing/subscriptions`, { prices }));
  }

  public async cancel() {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/billing/subscriptions`));
  }

  public async reActivate() {
    const resp = await lastValueFrom(
      this.http.get<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`)
    );
    const prices = resp.items.data.map(item => ({
      id: item.price.id,
      quantity: item.quantity,
    }));
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/billing/subscriptions`, { prices }));
  }

  public async getProration(price: Stripe.Price, promo?: Promotion): Promise<Proration> {
    const prices: { id: string; quantity?: number }[] = [{ id: price.id }];

    const [resp, mainPrices] = await Promise.all([
      lastValueFrom(
        this.http.patch<Stripe.Invoice>(`${this.config.apiEndpoint}/billing/subscriptions/prorate`, {
          prices,
          promotion_code: promo?.code,
        })
      ),
      firstValueFrom(this.pricingService.getPrices('main')),
    ]);

    if (!resp) return undefined;

    const allLines = resp.lines.data;

    // resp.lines = resp.lines.data;

    allLines.sort((a, b) => {
      const aIsMain = mainPrices.find(p => p.id === a.price.id);
      const bIsMain = mainPrices.find(p => p.id === b.price.id);
      if (aIsMain && !bIsMain) return -1;
      if (bIsMain && !aIsMain) return 1;
      if (a.type === 'subscription') return -1;
      if (b.type === 'subscription') return 1;
      return 0;
    });

    const products = [...new Set(allLines.map(line => line.price.product))];

    const lines = [];
    products.forEach(product => {
      allLines.filter(line => line.price.product === product).forEach(line => lines.push(line));
    });

    const subTotal = lines.reduce((total, line) => total + line.amount, 0);
    const usedBalance = -((resp.starting_balance || 0) - (resp.ending_balance || 0));
    const totalDiscount = lines.reduce((total, line) => total + line.discount_amounts[0]?.amount || 0, 0);

    let prorationDiscount: { start: number; end: number; promo: Promotion };

    for (const line of lines) {
      if (!line.discount_amounts[0]?.amount) continue; // we don't want to show a discount that has no impact on the invoice
      const discount = line.discount_amounts[0]?.discount;
      if (discount) {
        prorationDiscount = {
          start: discount.start * 1000,
          end: discount.end * 1000,
          promo: this.promotionService.convertPromo(discount),
        };
        break;
      }
    }

    return {
      ...resp,
      lines,
      subTotal,
      usedBalance,
      totalDiscount,
      discount: prorationDiscount,
    };
  }

  public extendTrial(days: number) {
    const timeLeftOnTrialMs = this.state.plan.trialEnd - Date.now();
    const timeLeftOnTrialDays = Math.ceil(timeLeftOnTrialMs / (1000 * 60 * 60 * 24));
    const params: UpdateSubscriptionBody = {
      trial_days: timeLeftOnTrialDays + days,
      prices: [
        {
          id: this.state.plan.price.id,
        },
      ],
    };
    return this.http.patch(`${this.config.apiEndpoint}/billing/subscriptions`, params).pipe(
      tap(() => {
        this.planService.getPlan();
      })
    );
  }

  public async updateBillingInfo(params: UpdateCustomerBodyDto) {
    const resp = await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/billing/customer`, params));
    this.loader.removeFuzzy('billingCustomer');
    this.planService.getPlan();
    return resp;
  }
}
